import type React from "react"
import { memo, useMemo, useState, useEffect } from "react"
import { Card, Space, Tag, Typography, Alert, Skeleton, Button } from "antd"
import { ExpandOutlined, CompressOutlined } from "@ant-design/icons"
import type { ValidationContentProps } from "./types"
import { ValidationAnalysis } from "./ValidationAnalysis"

export const ValidationContent: React.FC<ValidationContentProps> = memo(({ details, loading }) => {
  const [expanded, setExpanded] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 576);
  
  // Add effect to update isMobile on resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 576);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Show initial validation info immediately with safe null checks
  const validationStep = useMemo(() => {
    if (!details?.requestData?.processingSteps) return null;
    // Get the most recent validation step if there are multiple
    const validationSteps = details.requestData.processingSteps
      .filter((s) => s.step === "VALIDATION_ANALYSIS")
      .sort((a, b) => {
        // Sort by timestamp descending (newest first)
        const timestampA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
        const timestampB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
        return timestampB - timestampA;
      });
    return validationSteps[0] || null;
  }, [details])

  const validationState = useMemo(() => {
    if (!validationStep) return null;
    return {
      score: details?.validationScore ?? null,
      status: validationStep.status,
      platform: validationStep.metadata?.detectedPlatform || "unknown",
    }
  }, [details, validationStep])

  const validationTitle = useMemo(() => {
    if (!validationState) return null;
    
    const scoreValue = validationState.score !== null 
      ? (
        <Tag 
          color={validationState.score >= 0.9 ? "success" : "warning"}
          style={{ fontSize: isMobile ? '12px' : '14px', margin: isMobile ? '0 4px 0 0' : '0 8px 0 0' }}
        >
          Score: {(validationState.score * 100).toFixed(1)}%
        </Tag>
      ) : null;
      
    const platformTag = (
      <Tag 
        color={validationState.platform !== "unknown" ? "processing" : "default"}
        style={{ fontSize: isMobile ? '12px' : '14px' }}
      >
        {validationState.platform.toUpperCase()}
      </Tag>
    );
    
    return (
      <Space 
        align="center" 
        style={{ 
          width: '100%', 
          justifyContent: 'space-between',
          flexWrap: isMobile ? 'wrap' : 'nowrap'
        }}
      >
        <Space style={{ flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
          <Typography.Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>
            Validación
          </Typography.Text>
          {scoreValue}
          {platformTag}
        </Space>
        
        {isMobile && (
          <Button 
            type="text" 
            size="small" 
            icon={expanded ? <CompressOutlined /> : <ExpandOutlined />}
            onClick={() => setExpanded(!expanded)}
            style={{ padding: '4px' }}
          />
        )}
      </Space>
    );
  }, [validationState, isMobile, expanded]);


  // Show validation card immediately with loading state for content
  return (
    <Card
      size="small"
      title={validationTitle || (
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Typography.Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>Validación</Typography.Text>
          {isMobile && (
            <Button 
              type="text" 
              size="small" 
              icon={expanded ? <CompressOutlined /> : <ExpandOutlined />}
              onClick={() => setExpanded(!expanded)}
              style={{ padding: '4px' }}
            />
          )}
        </Space>
      )}
      className="step-card validation-card"
      styles={{
        body: { 
          padding: isMobile ? '8px 0' : '16px 0', /* Quitar padding lateral */
          display: expanded ? 'block' : 'none'
        },
        header: {
          paddingLeft: 0,
          paddingRight: 0,
          fontSize: isMobile ? '14px' : '16px',
          minHeight: isMobile ? '40px' : '48px'
        }
      }}
    >
      {loading ? (
        <Skeleton active paragraph={{ rows: isMobile ? 3 : 4 }} />
      ) : !details ? (
        <Alert
          type="warning"
          message="No hay detalles disponibles"
          description="La información de validación no está disponible en este momento."
          style={{ fontSize: isMobile ? '13px' : '14px' }}
        />
      ) : !validationStep ? (
        <Alert
          type="info"
          message="Validación pendiente"
          description="El análisis de validación aún no ha comenzado."
          style={{ fontSize: isMobile ? '13px' : '14px' }}
        />
      ) : (
        <>
          <ValidationAnalysis
            requestData={{
              retryHistory: details.requestData?.retryHistory || [],
              processingSteps: (details.requestData?.processingSteps || []).map((step) => ({
                ...step,
                timestamp: typeof step.timestamp === "string" ? step.timestamp : new Date(step.timestamp).toISOString(),
                metadata: {
                  ...(step.metadata || {}),
                  detectedPlatform: step.metadata?.detectedPlatform || "unknown",
                  extractedFields: step.metadata?.extractedFields || {},
                  comparedTransfers: step.metadata?.comparedTransfers || [],
                },
              })),
            }}
          />

          {validationState?.status === "REQUIRES_VALIDATION" && (
            <Alert 
              type="warning" 
              message="Requiere validación manual" 
              style={{ marginTop: isMobile ? '12px' : '16px', fontSize: isMobile ? '13px' : '14px' }} 
              showIcon 
            />
          )}

          {validationState?.status === "FAILED" && (
            <Alert
              type="error"
              message="Error en la validación"
              description={validationStep.metadata?.error || "Error desconocido"}
              style={{ marginTop: isMobile ? '12px' : '16px', fontSize: isMobile ? '13px' : '14px' }}
              showIcon
            />
          )}
        </>
      )}

      <style>{`
        .step-card {
          height: 100%;
          transition: all 0.3s ease;
        }
        
        .step-card .ant-card-body {
          padding: ${isMobile ? '8px' : '16px'};
        }
        
        .step-card .ant-alert {
          border-radius: 4px;
        }
        
        @media (max-width: 576px) {
          .validation-card .ant-card-head {
            min-height: 40px;
          }
          
          .validation-card .ant-card-head-title {
            padding: 8px 0;
          }
          
          .validation-card .ant-tag {
            margin-right: 4px;
            padding: 0 4px;
          }
          
          .validation-card .ant-alert-message {
            font-size: 13px;
          }
          
          .validation-card .ant-alert-description {
            font-size: 12px;
          }
        }
      `}</style>
    </Card>
  )
})

ValidationContent.displayName = "ValidationContent"

export default ValidationContent